import React, { useContext, useState } from 'react';
import styled from '@emotion/styled';

import { ModalContext } from '../../context/ModalContext';
import CloseIcon from '../Icons/CloseIcon';

interface ModalContainerProps {
  children: React.ReactNode;
  /** width of the modal */
  width?: string;
  /** padding inside the modal */
  padding?: string;
  /** margin-top of the modal */
  marginTop?: string;
  /** whether to require a confirmation before closing */
  confirm?: boolean;
  /** content to show inside the confirmation modal */
  granChild?: React.ReactNode;
  /** callback when the confirmation modal is finally closed */
  onConfirmClose?: () => void;
  maxwidth?: string;
}

const ModalContainer: React.FC<ModalContainerProps> = ({
  children,
  width,
  padding,
  marginTop,
  confirm = false,
  granChild,
  onConfirmClose,
  maxwidth,
}) => {
  const { displayModal } = useContext(ModalContext)!;
  const [showConfirm, setShowConfirm] = useState(false);

  const handlePrimaryClose = () => {
    if (confirm && !showConfirm) {
      // first click: open confirmation
      setShowConfirm(true);
    } else {
      // direct close (no confirm) or second click: close all
      displayModal(false);
    }
  };

  const handleConfirmClose = () => {
    // close everything
    displayModal(false);
    // run optional hook
    onConfirmClose?.();
  };

  return (
    <>
      {/* base modal */}
      <Container
        width={width}
        padding={padding}
        marginTop={marginTop}
        maxwidth={maxwidth}
      >
        <IconContainer onClick={handlePrimaryClose}>
          <CloseIcon />
        </IconContainer>
        {children}
      </Container>

      {/* confirmation overlay */}
      {confirm && showConfirm && (
        <Overlay onClick={(e) => e.stopPropagation()}>
          <Container width={width} padding={padding} marginTop={marginTop}>
            <IconContainer onClick={handleConfirmClose}>
              <CloseIcon />
            </IconContainer>
            {granChild}
          </Container>
        </Overlay>
      )}
    </>
  );
};

export default ModalContainer;

/** reused modal container styling. **/
interface IContainer {
  width?: string;
  padding?: string;
  marginTop?: string;
  maxwidth?: string;
}
const Container = styled.div<IContainer>`
  padding: ${({ padding }) => padding ?? '30px 77px 61px'};
  border-radius: 10px;
  background-color: white;
  margin-top: ${({ marginTop }) => marginTop ?? '60px'};
  margin-bottom: 60px;
  width: ${({ width }) => width ?? 'fit-content'};
  max-width: ${({ maxwidth }) => maxwidth ?? ''};
  display: flex;
  flex-direction: column;
  gap: 38px;
  
  @media (max-width: 884px) {
    width: 90vw;
    padding: 42px;
  }
  @media (max-width: 480px) {
    padding: 10px;
    width: 94vw;
  }
`;

/** semi-transparent overlay to gray out base modal **/
const Overlay = styled.div`
  position: absolute;
  height: 100vh;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
`;

/** close-icon wrapper **/
const IconContainer = styled.div`
  display: flex;
  align-self: flex-end;
  cursor: pointer;

  & > svg {
    transition: transform 0.3s ease-out;
    &:hover {
      transform: scale(1.1);
    }
  }
`;
